// Practice Details → Integrations: "Outreach SMS number override" (Jordan,
// 2026-10-26). Atlas texts every practice's patients from ONE shared Twilio number
// (Admin → Outreach, SharedSmsNumberTab); this card lets a practice save its own
// number instead, and shows which one is in effect. Webhook URLs and the test send
// live on the Admin tab, not here.
//
// Staff-visible (mounted where the other !readOnly cards are); the server enforces
// the role on every endpoint. No patient data anywhere here.

import React, { useEffect, useRef, useState } from "react";

import { fetchSmsNumber, looksLikePhone, removeSmsNumber, saveSmsNumber } from "./smsNumberApi";
import { SmsMessage, SmsStatus } from "./SmsNumberParts";
import "./SmsNumberCard.css";

export const NO_EFFECTIVE_NUMBER =
  "no number saved — the shared Atlas number is not set and this practice has no override";

export default function SmsNumberCard({ practiceId }) {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const busyRef = useRef(false);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  useEffect(() => {
    setConfig(null);
    setLoadError(null);
    setEditing(false);
    setError(null);
    setMessage(null);
    if (!practiceId) return undefined;
    let cancelled = false;
    fetchSmsNumber(practiceId)
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  if (!practiceId) return null;

  const startEdit = () => {
    setNumber((config.phone && config.phone.phoneE164) || "");
    setLabel((config.phone && config.phone.label) || "");
    setError(null);
    setMessage(null);
    setEditing(true);
  };

  // Shared by Save and Remove: the result is dropped if the biller has switched
  // practice meanwhile, so A's number never lands on B's card.
  const run = async (action, successText) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    const forPractice = practiceId;
    try {
      const saved = await action();
      if (currentPractice.current !== forPractice) return;
      setConfig(saved);
      setEditing(false);
      setMessage(successText(saved));
    } catch (err) {
      if (currentPractice.current !== forPractice) return;
      setError(err.message || "Could not save the outreach SMS number.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const save = () => {
    const trimmed = number.trim();
    if (!looksLikePhone(trimmed)) {
      setError("Enter the number as +1 then the digits (E.164), or a 10-digit US number.");
      return;
    }
    run(
      () => saveSmsNumber(practiceId, { phoneE164: trimmed, label }),
      (saved) =>
        `Saved. Patients of this practice are texted from ${
          (saved.phone && saved.phone.phoneE164) || saved.effectiveNumber || "the override"
        }.`,
    );
  };

  const remove = () =>
    run(
      () => removeSmsNumber(practiceId),
      (saved) =>
        saved.sharedNumber
          ? `Override removed. Patients are texted from the shared Atlas number ${saved.sharedNumber}.`
          : "Override removed. No shared Atlas number is set, so texting is off for this practice.",
    );

  return (
    <div className="integrations-section sms-card">
      <div className="sms-card-head">
        <h3>Outreach SMS number override</h3>
        {config && !editing && (
          <div className="sms-actions" style={{ marginTop: 0 }}>
            <button type="button" className="sms-btn" onClick={startEdit} disabled={busy}>
              {config.phone ? "Edit" : "Add override"}
            </button>
            {config.phone && (
              <button type="button" className="sms-btn sms-btn-danger" onClick={remove} disabled={busy}>
                {busy ? "Removing…" : "Remove"}
              </button>
            )}
          </div>
        )}
      </div>

      {loadError ? (
        <SmsMessage tone="error">{loadError}</SmsMessage>
      ) : !config ? (
        <p className="sms-muted">Loading…</p>
      ) : (
        <>
          <p className="sms-intro">
            {config.sharedNumber
              ? `Patients of this practice are texted from the shared Atlas number ${config.sharedNumber} unless an override is saved here.`
              : "No shared Atlas number is set yet (an admin sets it on Admin → Outreach). Patients of this practice are texted only if an override is saved here."}
          </p>

          <SmsStatus status={config} number={config.effectiveNumber} noNumberReason={NO_EFFECTIVE_NUMBER} />

          <SmsMessage tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </SmsMessage>

          {editing ? (
            <div className="sms-edit">
              <SmsMessage tone="error" onDismiss={() => setError(null)}>
                {error}
              </SmsMessage>
              <div className="sms-field">
                <label htmlFor={`sms-override-number-${practiceId}`}>Override number</label>
                <input
                  id={`sms-override-number-${practiceId}`}
                  className="sms-input"
                  type="tel"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="+14155550123"
                  autoComplete="off"
                  disabled={busy}
                />
              </div>
              <div className="sms-field">
                <label htmlFor={`sms-override-label-${practiceId}`}>Label</label>
                <input
                  id={`sms-override-label-${practiceId}`}
                  className="sms-input"
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Front desk line"
                  maxLength={80}
                  disabled={busy}
                />
              </div>
              <div className="sms-actions">
                <button type="button" className="sms-btn" onClick={() => setEditing(false)} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="sms-btn sms-btn-primary" onClick={save} disabled={busy}>
                  {busy ? "Saving…" : "Save override"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <SmsMessage tone="error" onDismiss={() => setError(null)}>
                {error}
              </SmsMessage>
              <div className="sms-number-row">
                <span className="sms-number-label">Texting from</span>
                {config.effectiveNumber ? (
                  <>
                    <span className="sms-number">{config.effectiveNumber}</span>
                    <span className="sms-number-source">
                      {config.phone ? "(this practice's override)" : "(shared Atlas number)"}
                    </span>
                  </>
                ) : (
                  <span className="sms-muted">no number</span>
                )}
              </div>
              {config.phone ? (
                <div className="sms-number-row">
                  <span className="sms-number-label">Override</span>
                  <span className="sms-number">{config.phone.phoneE164}</span>
                  {config.phone.label && <span className="sms-number-label">{config.phone.label}</span>}
                </div>
              ) : (
                <p className="sms-muted">No override saved for this practice.</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
